<template>
    <div v-if="show && loaded" class="nevs-popup-container">
        <div class="nevs-popup">
            <div class="popup-form">
                <div class="visit-time-block">
                    <div class="visit-time-title">{{ $LANG.Get('fields.checkedInAt') }}</div>
                    <div class="visit-time-fields">
                        <NevsDateField v-model="checkedIn.date" :error="validation.checked_in_date" width="50%"
                                       :label="$LANG.Get('fields.date') + ':'"></NevsDateField>
                        <NevsMaskedField v-model="checkedIn.time" :mask="'NN:NN:NN'" width="50%"
                                         :error="validation.checked_in_time"
                                         :label="$LANG.Get('fields.time') + ':'"></NevsMaskedField>
                    </div>
                </div>
                <div class="visit-time-block">
                    <div class="visit-time-title">{{ $LANG.Get('fields.checkedOutAt') }}</div>
                    <div class="visit-time-fields">
                        <NevsDateField v-model="checkedOut.date" :error="validation.checked_out_date" width="50%"
                                       :label="$LANG.Get('fields.date') + ':'"></NevsDateField>
                        <NevsMaskedField v-model="checkedOut.time" :mask="'NN:NN:NN'" width="50%"
                                         :error="validation.checked_out_time"
                                         :label="$LANG.Get('fields.time') + ':'"></NevsMaskedField>
                    </div>
                    <div class="visit-time-note">{{ $LANG.Get('labels.leaveEmptyIfStillPresent') }}</div>
                </div>
                <div v-if="localProducts.length > 0" class="edit-visit-products">
                    <div class="edit-visit-products-title">{{ $LANG.Get('fields.products') }}</div>
                    <table class="edit-visit-products-table">
                        <thead>
                        <tr>
                            <th class="edit-visit-products-check"></th>
                            <th>{{ $LANG.Get('fields.product') }}</th>
                            <th>{{ $LANG.Get('labels.available') }}</th>
                            <th class="edit-visit-products-quantity">{{ $LANG.Get('fields.quantity') }}</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(product, key) in localProducts" :key="key">
                            <td><NevsCheckbox v-model="product.selected"></NevsCheckbox></td>
                            <td>{{ product.product_name }}</td>
                            <td>{{ product.quantity_display }}</td>
                            <td>
                                <NevsNumberField v-if="product.selected" :decimal-places="2"
                                                 v-model="product.quantity" :error="product.error"></NevsNumberField>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div class="nevs-popup-buttons">
                <NevsButton class="error" @click="cancelClick()">
                    <i class="fa-solid fa-xmark"></i>
                    {{ $LANG.Get('buttons.cancel') }}
                </NevsButton>
                <NevsButton class="success" @click="okClick()">
                    <i class="fa-solid fa-check"></i>
                    {{ $LANG.Get('buttons.ok') }}
                </NevsButton>
            </div>
        </div>
    </div>
</template>

<script>
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCheckbox from "@/components/nevs/NevsCheckbox.vue";
import NevsNumberField from "@/components/nevs/NevsNumberField.vue";
import NevsDateField from "@/components/nevs/NevsDateField.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";

export default {
    name: "ModalVisit",
    components: {NevsMaskedField, NevsDateField, NevsNumberField, NevsCheckbox, NevsButton},
    props: {
        show: {
            type: Boolean,
            default: false
        },
        // Only needed when adding; an edit takes the person from the visit itself.
        personId: {
            type: [Number, String],
            default: null
        },
        // null opens the pop-up in "add" mode, an existing visit in "edit" mode.
        visit: {
            type: Object,
            default: null
        }
    },
    emits: ['done'],
    data() {
        return {
            loaded: false,
            checkedIn: {
                date: '',
                time: ''
            },
            checkedOut: {
                date: '',
                time: ''
            },
            localProducts: [],
            validation: {
                checked_in_date: '',
                checked_in_time: '',
                checked_out_date: '',
                checked_out_time: ''
            }
        }
    },
    watch: {
        show() {
            this.loaded = false;
            this.checkedIn = {date: '', time: ''};
            this.checkedOut = {date: '', time: ''};
            this.localProducts = [];
            this.clearValidation();
            if (!this.show) return;

            // The times and the editable product pool are always loaded from the server, so this pop-up works
            // the same whichever grid opened it, rather than depending on the row's shape.
            let vm = this;
            this.$API.APICall('get', this.infoEndpoint(), {}, (data, success) => {
                if (success) {
                    vm.checkedIn = vm.splitDateTime(data.checked_in_at);
                    vm.checkedOut = vm.splitDateTime(data.checked_out_at);
                    vm.applyProducts(data.products, false);
                    vm.$nextTick(() => {
                        vm.loaded = true;
                    });
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        'checkedIn.date'() {
            // Which memberships supply products depends on the arrival date, so moving it rebuilds the pool.
            if (!this.loaded) return;
            if (!/^\d{4}-\d{2}-\d{2}$/.test(this.checkedIn.date)) return;
            this.refreshProducts();
        }
    },
    methods: {
        infoEndpoint() {
            return this.visit !== null
                ? 'visits/edit-info/' + this.visit.id
                : 'visits/create-info/' + this.personId;
        },
        splitDateTime(value) {
            if (!value) return {date: '', time: ''};
            let parts = value.split(' ');
            return {date: parts[0], time: parts.length > 1 ? parts[1] : ''};
        },
        clearValidation() {
            this.validation.checked_in_date = '';
            this.validation.checked_in_time = '';
            this.validation.checked_out_date = '';
            this.validation.checked_out_time = '';
        },
        /**
         * Rebuilds the product rows from a freshly loaded pool. On a refresh the user's current selection and
         * quantities are carried over, so changing the arrival date doesn't discard what they already ticked.
         */
        applyProducts(products, keepSelection) {
            let previous = {};
            if (keepSelection) {
                for (let product of this.localProducts) {
                    previous[product.product_id] = product;
                }
            }
            this.localProducts = products.map((product) => {
                let carried = previous[product.product_id];
                return {
                    product_id: product.product_id,
                    product_name: product.product_name,
                    quantity_display: product.quantity_display,
                    quantity_available: product.quantity_available,
                    selected: carried !== undefined ? carried.selected : product.on_visit > 0,
                    quantity: carried !== undefined ? carried.quantity : (product.on_visit > 0 ? product.on_visit : 1),
                    error: ''
                };
            });
        },
        refreshProducts() {
            let vm = this;
            this.$API.APICall('get', this.infoEndpoint(), {date: this.checkedIn.date}, (data, success) => {
                if (success) {
                    vm.applyProducts(data.products, true);
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        validTime(value) {
            if (!/^\d{2}:\d{2}:\d{2}$/.test(value)) return false;
            let parts = value.split(':');
            return parseInt(parts[0]) < 24 && parseInt(parts[1]) < 60 && parseInt(parts[2]) < 60;
        },
        cancelClick() {
            this.$emit('done', false);
        },
        okClick() {
            this.clearValidation();
            let valid = true;

            if (!/^\d{4}-\d{2}-\d{2}$/.test(this.checkedIn.date)) {
                this.validation.checked_in_date = this.$LANG.Get('labels.mandatoryField');
                valid = false;
            }
            if (!this.validTime(this.checkedIn.time)) {
                this.validation.checked_in_time = this.$LANG.Get('labels.invalidTimeWithSeconds');
                valid = false;
            }

            // An empty check-out means the visit is still open; a half-filled one is a mistake.
            let hasCheckedOut = this.checkedOut.date !== '' || this.checkedOut.time !== '';
            if (hasCheckedOut) {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(this.checkedOut.date)) {
                    this.validation.checked_out_date = this.$LANG.Get('labels.mandatoryField');
                    valid = false;
                }
                if (!this.validTime(this.checkedOut.time)) {
                    this.validation.checked_out_time = this.$LANG.Get('labels.invalidTimeWithSeconds');
                    valid = false;
                }
            }
            if (valid && hasCheckedOut
                && this.checkedOut.date + ' ' + this.checkedOut.time < this.checkedIn.date + ' ' + this.checkedIn.time) {
                this.validation.checked_out_time = this.$LANG.Get('labels.checkedOutBeforeCheckedIn');
                valid = false;
            }

            let products = [];
            for (let product of this.localProducts) {
                product.error = '';
                if (!product.selected) continue;
                if (product.quantity === null || product.quantity <= 0 || product.quantity > product.quantity_available) {
                    product.error = this.$LANG.Get('labels.notEnoughAvailable');
                    valid = false;
                    continue;
                }
                products.push({product_id: product.product_id, quantity: product.quantity});
            }
            if (!valid) return;

            let payload = {
                checked_in_at: this.checkedIn.date + ' ' + this.checkedIn.time,
                checked_out_at: hasCheckedOut ? this.checkedOut.date + ' ' + this.checkedOut.time : '',
                products: products
            };
            if (this.visit === null) {
                payload.person_id = parseInt(this.personId);
            }

            let vm = this;
            let method = this.visit === null ? 'post' : 'put';
            let action = this.visit === null ? 'visits' : 'visits/' + this.visit.id;
            this.$API.APICall(method, action, payload, (data, success) => {
                if (success) {
                    vm.$emit('done', true);
                } else if (data.error === 'insufficient_products') {
                    vm.$LOCAL_BUS.TriggerEvent('popup', {text: vm.$LANG.Get('alerts.insufficientProducts'), type: 'alert'});
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.nevs-popup {
    max-width: 500px;
}

.popup-form {
    padding: 10px;
}

.visit-time-block {
    margin-bottom: 10px;
}

.visit-time-title {
    font-weight: 600;
    /* Lines up with the 5px the fields below carry as their own padding. */
    padding: 0 5px 4px;
}

.visit-time-fields {
    display: flex;
}

.visit-time-note {
    color: $ink-muted;
    font-size: 0.85em;
    padding: 0 5px;
}

.edit-visit-products {
    margin-top: 10px;
    border: 1px solid $sand-300;
    border-radius: 5px;
    padding: 10px 12px;
}

.edit-visit-products-title {
    font-weight: 600;
    margin-bottom: 8px;
}

.edit-visit-products-table {
    width: 100%;
    border-collapse: collapse;
}

.edit-visit-products-table th, .edit-visit-products-table td {
    text-align: left;
    padding: 5px 10px 5px 0;
    vertical-align: middle;
}

.edit-visit-products-table th {
    font-size: 0.85em;
}

.edit-visit-products-check {
    width: 30px;
}

.edit-visit-products-quantity {
    width: 110px;
}
</style>
